import { ProductData, ModelData, Product } from '@ikea-aoa/component-view-3d';
import { FamilyType } from '@ikea-aoa/component-view-3d/lib/main/connection-manager/types';
import { IProductContent, IShoppingProduct } from '@inter-ikea-kompis/types';
import { ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';
import {
	SofaConfigurationItem,
	CoverItem,
	MissingProductItem,
	RangeSettings,
	ModelsQuantity,
	Settings,
	ShoppingItem,
} from 'sofa-reborn-react';

export class ProductUtils {
	public static getColorIdFromProduct(products: ProductData, productId: string): string | null {
		const productWithColorId = Object.keys(products).find((id) => {
			const product = products[id];
			if (id === productId || productId === product.previousId) {
				return true;
			}
			if (product && product.sprItems) {
				const sprItemWithColorId = product.sprItems.some((sprItem) => {
					return sprItem.itemno === productId;
				});
				return sprItemWithColorId;
			}

			return false;
		});

		return productWithColorId ? (products[productWithColorId].filter.color as string) : null;
	}

	public static getProductId(
		products: ProductData,
		modelId: string,
		colorId: string
	): string | null {
		const productId = Object.keys(products).find((productId) => {
			const product = products[productId];
			return product && product.modelId === modelId && product.filter.color === colorId;
		});

		return productId ? productId : null;
	}

	public static getProduct(
		products: ProductData,
		modelId: string,
		colorId: string
	): Product | null {
		const product = Object.values(products).find(
			(product) => product && product.modelId === modelId && product.filter.color === colorId
		);

		return product ? product : null;
	}

	public static hasFeature(products: ProductData, models: ModelData, productId: string): boolean {
		const product = products[productId];
		if (!product) {
			return false;
		}
		const model = models[product.modelId];
		return model?.logic?.hasFeature || false;
	}

	public static hasFeatureSleep(
		products: ProductData,
		models: ModelData,
		productId: string,
		rangeSettings: RangeSettings
	): boolean {
		if (this.hasFeature(products, models, productId)) {
			const product = products[productId];
			const model = models[product.modelId];
			return rangeSettings.modelsWithSleepFunction?.includes(model.modelId) || false;
		}
		return false;
	}

	public static hasFeatureStorage(
		products: ProductData,
		models: ModelData,
		productId: string,
		rangeSettings: RangeSettings
	): boolean {
		if (this.hasFeature(products, models, productId)) {
			const product = products[productId];
			const model = models[product.modelId];
			return rangeSettings.modelsWithStorageFunctions?.includes(model.modelId) || false;
		}
		return false;
	}

	public static getIkeaProductId(
		product: IProductContent,
		localSofabedMattressId: string | null,
		newLocalSofabedMattressId: string | null,
		globalSofabedMattressId: string[],
		sofabedMattressId: string | null,
		useLocalIds: boolean,
		localIds: string[] | null
	): string {
		if (product.itemNoLocal === sofabedMattressId) {
			return product.itemNoLocal || '';
		}
		const globalMattressId =
			product.itemNoLocal && globalSofabedMattressId.length > 0
				? globalSofabedMattressId.find((item) => item === product.itemNoLocal) || false
				: false;
		if (
			globalSofabedMattressId.length > 1 &&
			localSofabedMattressId &&
			localSofabedMattressId === product.itemNoLocal
		) {
			return globalSofabedMattressId[0];
		} else if (
			globalSofabedMattressId.length > 1 &&
			newLocalSofabedMattressId &&
			newLocalSofabedMattressId === product.itemNoLocal
		) {
			return globalSofabedMattressId[1];
		} else if (globalMattressId) {
			return globalMattressId;
		}
		return useLocalIds || localIds?.includes(product.itemNoLocal as string)
			? product.itemNoLocal || ''
			: product.itemNoGlobal || product.itemNoLocal || '';
	}

	public static getSprIdValid(products: ProductData, productId: string): boolean {
		const cleanProductId = productId.endsWith('_mirror')
			? productId.replace('_mirror', '')
			: productId;
		const product = products[cleanProductId];
		return !product.sprIdInvalid && product.filter.noArticleId !== true;
	}

	public static getModelIdsForConfiguration(
		products: ProductData,
		missingProducts: MissingProductItem[],
		sofaConfigurationItems: SofaConfigurationItem[],
		shouldColorAllModels: boolean,
		getAllModels: boolean
	): string[] {
		let modelIds: string[] = [];
		sofaConfigurationItems.forEach((child: SofaConfigurationItem) => {
			if (child && child.children) {
				const product = Object.keys(products).find(
					(product) =>
						products[product].productId === child.articleId ||
						products[product].previousId === child.articleId
				);
				const missingProduct = missingProducts.find(
					(product) =>
						product.productId === child.articleId || product.previousId === child.articleId
				);
				if ((product && shouldColorAllModels) || (product && getAllModels)) {
					if (!modelIds.includes(products[product].modelId) || getAllModels) {
						modelIds.push(products[product].modelId);
					}
				}

				if (missingProduct) {
					if (!modelIds.includes(missingProduct.modelId) || getAllModels) {
						modelIds.push(missingProduct.modelId);
					}
				}

				modelIds = modelIds.concat(
					this.getModelIdsForConfiguration(
						products,
						missingProducts,
						child.children,
						shouldColorAllModels,
						getAllModels
					)
				);
			}
		});

		return modelIds;
	}

	public static getAllAvailableCovers(
		products: ProductData,
		coverItems: CoverItem[],
		configurationModelIds: string[]
	): CoverItem[] {
		const filteredCoverItems = coverItems.filter((coverItem) => {
			return configurationModelIds.every((modelId) => {
				const productItem = this.getProductFromModelIdAndCover(
					products,
					modelId,
					coverItem.coverName
				);
				return productItem ? productItem : false;
			});
		});
		return filteredCoverItems;
	}

	public static getProductFromModelIdAndCover(
		products: ProductData,
		modelId: string,
		coverName: string
	): Product | null {
		const product = Object.keys(products).find((product) => {
			const productItem = products[product];
			return productItem.modelId === modelId && productItem.filter.color === coverName;
		});
		return product ? products[product] : null;
	}

	public static getNewConfiguration(
		products: ProductData,
		missingProducts: MissingProductItem[],
		sofaConfigurationItems: SofaConfigurationItem[],
		removeMissingProducts: boolean,
		shouldColorAllModels: boolean,
		coverItem?: CoverItem
	): SofaConfigurationItem[] {
		const newConfiguration: SofaConfigurationItem[] = [];

		sofaConfigurationItems.forEach((child: SofaConfigurationItem) => {
			if (child && child.children) {
				const articleId = child.articleId?.replace('G', '');
				const product = Object.keys(products).find(
					(product) =>
						products[product].productId === articleId || products[product].previousId === articleId
				);
				const missingProduct = missingProducts.find(
					(product) => product.productId === articleId || product.previousId === articleId
				);

				if (removeMissingProducts) {
					if (missingProduct) {
						return;
					}
				} else if (product && shouldColorAllModels && coverItem) {
					const updatedProduct = this.getProductFromModelIdAndCover(
						products,
						products[product].modelId,
						coverItem.coverName
					);
					if (updatedProduct) {
						child.articleId = updatedProduct.productId;
					}
				} else if (missingProduct && coverItem) {
					const newProduct = this.getProductFromModelIdAndCover(
						products,
						missingProduct.modelId,
						coverItem.coverName
					);
					if (newProduct) {
						child.articleId = newProduct.productId;
					}
				}
				child['children'] = this.getNewConfiguration(
					products,
					missingProducts,
					child.children,
					removeMissingProducts,
					shouldColorAllModels,
					coverItem
				);
				newConfiguration.push(child);
			}
		});
		return newConfiguration;
	}

	public static getColor(product: Product): string | null {
		return product && product.filter && product.filter.color ? product.filter.color : null;
	}

	public static isPreviousId(products: ProductData, articleId: string): boolean {
		let isOld = false;
		if (!products[articleId]) {
			Object.values(products).forEach((product: Product) => {
				if (product.previousId === articleId) {
					isOld = true;
				}
			});
		}
		return isOld;
	}

	public static getNewIdFromPrevious(products: ProductData, previousId: string): string | null {
		Object.values(products).forEach((product: Product) => {
			if (product.previousId === previousId) {
				return product.productId;
			}
			return null;
		});
		return null;
	}

	public static containsFamily(products: Product[], familyType: FamilyType): boolean {
		return products.some((product) => product.filter?.family === familyType);
	}

	public static modelIdContains(products: Product[], type: string): boolean {
		return products.some((product) => product.modelId.includes(type));
	}

	public static containsStorage(
		products: Product[],
		storageFamilies: FamilyType[],
		rangeSettings: RangeSettings
	): boolean {
		let containsStorage = false;
		const isModularSofa =
			rangeSettings.onlyRemoveOuterModules !== undefined
				? !rangeSettings.onlyRemoveOuterModules
				: false;
		storageFamilies.forEach((family) => {
			if (!containsStorage) {
				containsStorage =
					(!isModularSofa && ProductUtils.containsFamily(products, family)) ||
					ProductUtils.modelIdContains(products, 'STORAGE');
			}
		});
		return containsStorage;
	}

	public static hasSofaPart(products: ProductData, shoppingItems: ShoppingItem[]): boolean {
		const items = shoppingItems.filter((item) => {
			const family = this.getFamily(products, item.id);
			return family !== 'FOOTSTOOL';
		});
		return items.length > 0;
	}

	public static hasArmrest(products: ProductData, shoppingItems: ShoppingItem[]): boolean {
		const items = shoppingItems.filter((item) => {
			const family = this.getFamily(products, item.id);
			return family === 'ARMREST';
		});
		return items.length > 0;
	}

	private static getFamily = (products: ProductData, productId: string): FamilyType | null => {
		const id = Object.keys(products).find((product) => {
			const productData = products[product];
			return productData.productId === productId || productData.previousId === productId;
		});

		if (id) {
			const data = products[id];

			return data && data.filter && data.filter.family ? data.filter.family : null;
		}
		return null;
	};

	public static getModelQuantity(modelIds: string[]): ModelsQuantity {
		const modelsWithQuantity = {};

		modelIds.forEach((x) => {
			modelsWithQuantity[x] = (modelsWithQuantity[x] || 0) + 1;
		});

		return modelsWithQuantity;
	}

	public static async getAdditionalCoverProducts(
		settings: Settings,
		items: ShoppingItem[]
	): Promise<IShoppingProduct[]> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const products: IShoppingProduct[] = await productService.getShoppingProducts(
			items,
			KompisDefaultFields
		);
		const validProducts = products.filter(
			(prod) => prod.product.valid && prod.product.content?.mainTypeCode === '00062'
		);

		return validProducts;
	}

	public static getCoverOnlyArticles = (shoppingProducts: IShoppingProduct[]): ShoppingItem[] => {
		const articleItems: ShoppingItem[] = [];

		shoppingProducts.forEach((shoppingProduct) => {
			if (shoppingProduct.product?.itemId?.includes('ART')) {
				articleItems.push({
					id: shoppingProduct.product.itemId.replace('ART-', ''),
					quantity: shoppingProduct.quantity,
					type: 'ART',
				});
			} else {
				shoppingProduct.product.content?.child?.forEach((child) => {
					articleItems.push({
						id: child.itemId.replace('ART-', ''),
						quantity: child.quantity * shoppingProduct.quantity,
						type: 'ART',
					});
				});
			}
		});

		return articleItems;
	};
}
